import { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Alert } from 'antd';
import HeartLoader from '@/shared/ui/HeartLoader';

import DicomList from '@/features/study-detail/DicomList/DicomList.tsx';
import { MultiViewerContainer } from '@/features/study-detail/Viewer/MultiViewerContainer';
import { ViewerProvider } from '@/features/study-detail/Viewer/ViewerProvider';
import { ViewerErrorBoundary } from '@/features/study-detail/Viewer/ViewerErrorBoundary';
import DicomButtons from '@/features/study-detail/DicomButtons/DicomButtons';
import { useResizableBarHeight } from '@/features/study-detail/DicomList/useResizableBarHeight';
import { usePopupChannel } from '@/features/study-detail/hooks/usePopupChannel';

import { useGetStudyQuery } from '@/state/slices/studies';
import styles from './ViewerPopup.module.css';

/**
 * Standalone Viewer page for popup window.
 * URL: /studies/:studyUuid/viewer
 *
 * Syncs with the main window via BroadcastChannel:
 * - Follows study changes when user switches studies in the main window
 * - Notifies main window on close so it can reattach the inline viewer
 */
export default function ViewerPopup() {
  const { studyUuid } = useParams<{
    assignmentUuid: string;
    studyUuid: string;
  }>();

  const goToAnnotationRef = useRef<
    ((dicomUuid: string, frameNumber: number) => void) | null
  >(null);
  const { handleClose, isIdle } = usePopupChannel({ goToAnnotationRef });
  const dicomBar = useResizableBarHeight({
    storageKey: 'dicomBarHeightPopup',
    min: 124,
    defaultHeight: 124,
  });

  // Fetch study data via RTK Query
  const {
    data: study,
    isLoading,
    error,
  } = useGetStudyQuery({ uuid: studyUuid! }, { skip: !studyUuid });

  if (isIdle) {
    return (
      <div className={styles.container}>
        <div className={styles.idleOverlay}>
          <p className={styles.idleText}>
            Open a study in the Reader to view DICOMs
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingOverlay}>
          <HeartLoader showText />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container} style={{ padding: '20px' }}>
        <Alert
          message="Error Loading Study"
          description={`Failed to load study data. ${
            error && 'data' in error
              ? JSON.stringify(error.data)
              : 'Please try again.'
          }`}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (!study) {
    return (
      <div className={styles.container} style={{ padding: '20px' }}>
        <Alert
          message="No Study Data"
          description="No study data available."
          type="warning"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ViewerProvider
        key={study.uuid}
        studyUuid={study.uuid}
        dicoms={study.dicoms}
        goToAnnotationRef={goToAnnotationRef}
      >
        <div className={styles.viewerPanel}>
          <ViewerErrorBoundary>
            <MultiViewerContainer
              isDetached
              onToggleDetach={handleClose}
              detachDisabled={false}
            />
          </ViewerErrorBoundary>
        </div>

        <div
          className={styles.dicomResizeHandle}
          title="Drag to resize the thumbnail bar"
          {...dicomBar.handleProps}
        />
        <div
          className={styles.dicomListPanel}
          style={{ height: dicomBar.height }}
        >
          <DicomList dicoms={study.dicoms} />
          <DicomButtons popupWindow={window} />
        </div>
      </ViewerProvider>
    </div>
  );
}
